import React, { PureComponent } from "react";
import connect from "react-redux/es/connect/connect";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import { Part } from "models/Part";

import ConfiguratorDropdown from "components/ConfiguratorDropdown";
import PartImage from "components/PartImage";

import "./FilterSelection.css";
import "./AccessorySelection.css";

const ImageBlock = ({ choice, bundle, getPartString, smallScreen }) => {
  const imageWidth = bundle.length > 0 ? 100 / bundle.length : 100;
  const comment = choice
    .map((part) =>
      part.showComment ? getPartString(`${part.partNo}-comment`) : "",
    )
    .join(" ");
  if (choice.length === 0) {
    return null;
  }
  const stacked = smallScreen && bundle.length > 1 ? "stacked" : "";
  return (
    <div
      className={`part-image-block ${stacked}`}
      style={{ display: choice.length > 0 ? "block" : "none" }}
    >
      {bundle.length > 0 ? (
        bundle.map((part) => (
          <div
            key={part.partNo}
            className={`image-container ${stacked} ${smallScreen && part.isOpaqueKit() ? "vertical" : ""}`}
            style={{
              width: `${stacked ? "90" : imageWidth}%`,
            }}
          >
            <PartImage part={part} smallScreen={smallScreen} />
          </div>
        ))
      ) : (
        <div
          key={choice[0]}
          className="image-container"
          style={{
            width: "100%",
          }}
        >
          <PartImage caption={choice[0]} smallScreen={smallScreen} />
        </div>
      )}
      {comment !== "" ? <p className="comment">{comment}</p> : null}
    </div>
  );
};

ImageBlock.defaultProps = {
  choice: [],
  bundle: [],
  smallScreen: false,
};
ImageBlock.propTypes = {
  choice: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Part)]),
  ),
  bundle: PropTypes.arrayOf(PropTypes.instanceOf(Part)),
  getPartString: PropTypes.func.isRequired,
  smallScreen: PropTypes.bool,
};

class AccessorySelection extends PureComponent {
  onSelect = (id, key) => {
    const { onSelect, options } = this.props;
    onSelect(id, options[key]);
  };

  getPartString = (partNo) => {
    const { getString } = this.props;
    return getString(partNo, "part");
  };

  render() {
    const { id, choice, bundle, options, getString, placeholder, onDrop } =
      this.props;
    // Get the actual labels and keys for each option
    const getOptionMeta = (option) => {
      if (option instanceof Part && option.scratchData) {
        const { disabled, optional, underline } = option.scratchData;
        return {
          disabled,
          optional,
          underline,
        };
      }
      return null;
    };
    const optionsMeta = options.map(getOptionMeta);
    const keys = options.map((option, idx) => idx);
    const labels = options.map((option, idx) => {
      if (Array.isArray(option)) {
        let i = option.length - 1;
        while (i >= 0 && !(option[i] instanceof Part)) i--;
        // insert options meta for this part
        if (i >= 0) {
          optionsMeta[idx] = getOptionMeta(option[i]);
        }
        return i >= 0 ? `${option[i].partNo}-label` : option[0];
      }
      if (option instanceof Part) {
        return `${option.partNo}-label`;
      }
      return option;
    });
    // combine labels for multiple part selections
    let choiceText = getString(placeholder, "accessories");
    if (choice.length > 0) {
      choiceText = choice
        .map((part, idx) => {
          if (typeof part === "string") {
            // ie. 'no-extension-cable'
            return getString(part, "accessories");
          }
          // wrap 2nd part in parens
          const label = this.getPartString(`${part.partNo}-label`);
          return idx === 0 ? label : `(${label})`;
        })
        .join(" ");
    }
    // helpers
    const dropAreaFiller = () => [
      <div
        key="arrow"
        className="arrow-right"
        style={{ borderLeftColor: "white" }}
      />,
      <span
        key="label"
        dangerouslySetInnerHTML={{
          __html:
            choice.length > 0
              ? choiceText
              : getString(placeholder, "accessories"),
        }}
      />,
    ];
    // For kits with their own images (that shows all the parts), don't send the bundle to the image block
    const imageBlockBundle = () =>
      choice[0] instanceof Part && choice[0].isOpaqueKit()
        ? [choice[0]]
        : bundle;
    return (
      <div className="dropdown accessory-dropdown">
        {choice.length > 0 && options.length > 1 ? (
          <button
            type="button"
            onClick={() => onDrop(id)}
            className="dropbtn accessory-dropbtn"
          >
            {dropAreaFiller()}
          </button>
        ) : (
          <div className="dropbtn accessory-dropbtn">{dropAreaFiller()}</div>
        )}
        {choice.length === 0 ? (
          <div className="dropdown-content accessory-dropdown-content">
            <ConfiguratorDropdown
              {...this.props}
              getString={getString}
              keys={keys}
              options={labels}
              meta={optionsMeta}
              onSelect={this.onSelect}
            />
          </div>
        ) : null}
        <MediaQuery key="lg" query="(min-width: 768px)">
          <ImageBlock
            choice={choice}
            bundle={imageBlockBundle()}
            getPartString={this.getPartString}
          />
        </MediaQuery>
        <MediaQuery key="sm" query="(max-width: 767px)">
          <ImageBlock
            choice={choice}
            bundle={imageBlockBundle()}
            getPartString={this.getPartString}
            smallScreen
          />
        </MediaQuery>
      </div>
    );
  }
}

AccessorySelection.defaultProps = {
  choice: [],
  bundle: null,
  caption: "",
};
AccessorySelection.propTypes = {
  id: PropTypes.string.isRequired,
  choice: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Part)]),
  ),
  bundle: PropTypes.arrayOf(PropTypes.instanceOf(Part)),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Part),
      PropTypes.arrayOf(PropTypes.instanceOf(Part)),
    ]),
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  getString: PropTypes.func.isRequired,
};

export default connect((state) => ({
  getString: state.strings.getString,
}))(AccessorySelection);
